import React, { useState, useEffect, FormEvent } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Lock, User, Building2, AlertCircle, Check, Eye, EyeOff } from 'lucide-react';
import api, { apiErrorText } from '../../utils/api';
import { useTranslation } from 'react-i18next';
import KVKKConsentModal from '../../components/KVKKConsentModal';

interface InviteInfo {
  email: string;
  name: string;
  role: string;
  company_name: string;
  expires_at: string;
}

const RegisterInvite: React.FC = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [inviteInfo, setInviteInfo] = useState<InviteInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [kvkkConsent, setKvkkConsent] = useState(false);
  const [showKvkkModal, setShowKvkkModal] = useState(false);

  useEffect(() => {
    if (!token) {
      setError(t('registerInvite.noToken'));
      setLoading(false);
      return;
    }

    const fetchInviteInfo = async () => {
      try {
        const response = await api.get(`/api/team/invite-info?token=${token}`);
        setInviteInfo(response.data);
      } catch (err: any) {
        const detail = apiErrorText(err?.response?.data?.detail) || t('registerInvite.invalidToken');
        setError(detail);
      } finally {
        setLoading(false);
      }
    };

    fetchInviteInfo();
  }, [token, t]);

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setFormError(null);

    // Validation
    if (password.length < 8) {
      setFormError(t('registerInvite.passwordTooShort'));
      return;
    }

    if (password !== confirmPassword) {
      setFormError(t('registerInvite.passwordMismatch'));
      return;
    }

    // KVKK onam kontrolü
    if (!kvkkConsent) {
      setShowKvkkModal(true);
      return;
    }

    setSubmitting(true);
    try {
      await api.post('/api/team/register-invite', {
        token,
        password,
        kvkk_consent: kvkkConsent,
        kvkk_consent_date: new Date().toISOString()
      });
      setSuccess(true);
    } catch (err: any) {
      const detail = apiErrorText(err?.response?.data?.detail) || t('registerInvite.registerError');
      setFormError(detail);
    } finally {
      setSubmitting(false);
    }
  };

  const handleKvkkAccept = () => {
    setKvkkConsent(true);
    setShowKvkkModal(false);
    // Kayıt işlemini tekrar başlat
    handleSubmit(new Event('submit') as any);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-lotus-gold"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-lg p-8 text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="text-red-600" size={32} />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">{t('registerInvite.errorTitle')}</h1>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => navigate('/login')}
            className="btn-primary"
          >
            {t('registerInvite.goToLogin')}
          </button>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-lg p-8 text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Check className="text-green-600" size={32} />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">{t('registerInvite.successTitle')}</h1>
          <p className="text-gray-600 mb-6">
            {t('registerInvite.successMessage')}
          </p>
          <button
            onClick={() => navigate('/login')}
            className="btn-primary"
          >
            {t('registerInvite.login')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-lg p-8">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-lotus-gold/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <User className="text-lotus-gold" size={32} />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">{t('registerInvite.title')}</h1>
          <p className="text-gray-600">
            {t('registerInvite.subtitle')}
          </p>
        </div>

        {/* Invite Info */}
        {inviteInfo && (
          <div className="bg-gray-50 rounded-lg p-4 mb-6 space-y-3">
            <div className="flex items-center gap-3">
              <Building2 className="text-gray-400" size={20} />
              <div>
                <p className="text-xs text-gray-500">{t('registerInvite.company')}</p>
                <p className="font-medium text-gray-900">{inviteInfo.company_name}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <User className="text-gray-400" size={20} />
              <div>
                <p className="text-xs text-gray-500">{t('registerInvite.name')}</p>
                <p className="font-medium text-gray-900">{inviteInfo.name}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Lock className="text-gray-400" size={20} />
              <div>
                <p className="text-xs text-gray-500">{t('registerInvite.role')}</p>
                <p className="font-medium text-gray-900">
                  {t(`team.roles.${inviteInfo.role}`, inviteInfo.role.toUpperCase())}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('registerInvite.email')}
            </label>
            <input
              type="email"
              value={inviteInfo?.email || ''}
              disabled
              className="w-full rounded-lg border border-gray-300 px-3 py-2 bg-gray-100 text-gray-600"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('registerInvite.password')}
            </label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 pr-10 focus:border-lotus-gold focus:outline-none"
                placeholder={t('registerInvite.passwordPlaceholder')}
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('registerInvite.confirmPassword')}
            </label>
            <input
              type={showPassword ? 'text' : 'password'}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-lotus-gold focus:outline-none"
              placeholder={t('registerInvite.confirmPasswordPlaceholder')}
              required
            />
          </div>

          {formError && (
            <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
              <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
              <p>{formError}</p>
            </div>
          )}

          {/* KVKK Consent Checkbox */}
          <div className="mb-4">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                id="kvkk-consent-checkbox"
                checked={kvkkConsent}
                onChange={(e) => setKvkkConsent(e.target.checked)}
                className="w-4 h-4 text-lotus-gold rounded mt-1"
              />
              <label htmlFor="kvkk-consent-checkbox" className="text-sm text-gray-700 leading-relaxed">
                <a 
                  href="/legal/kvkk-aydinlatma" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-lotus-gold underline hover:text-lotus-gold/80"
                >
                  {t('registerInvite.kvkkDocLink')}
                </a>
                {' '}{t('registerInvite.kvkkConsentText')}
              </label>
            </label>
          </div>

          <button
            type="submit"
            disabled={submitting || !kvkkConsent}
            className="w-full btn-primary flex items-center justify-center gap-2"
          >
            {submitting ? (
              <>
                <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                {t('registerInvite.creating')}
              </>
            ) : (
              <>
                <Check size={20} />
                {t('registerInvite.createAccount')}
              </>
            )}
          </button>
        </form>

        <p className="text-center text-sm text-gray-500 mt-6">
          {t('registerInvite.alreadyHaveAccount')}{' '}
          <button
            onClick={() => navigate('/login')}
            className="text-lotus-gold hover:underline font-medium"
          >
            {t('registerInvite.loginLink')}
          </button>
        </p>
      </div>

      {/* KVKK Consent Modal */}
      <KVKKConsentModal
        isOpen={showKvkkModal}
        onClose={() => setShowKvkkModal(false)}
        onAccept={handleKvkkAccept}
        consentType="register"
      />
    </div>
  );
};

export default RegisterInvite;
